import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";

const usd = (v, d = 0) => `$${Math.abs(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const sgn = (v, d = 2) => `${v < 0 ? "-" : "+"}${usd(v, d)}`;
const pct = (v) => (v == null ? "--" : `${v < 0 ? "" : "+"}${Number(v).toFixed(2)}%`);
const tone = (v) => (v == null || v === 0 ? "text-cyan-50" : v > 0 ? "text-[#2CF6B3]" : "text-[#FF3D68]");
const px = (v) => (v == null ? "--" : v >= 100 ? Number(v).toFixed(2) : Number(v).toPrecision(5));

export const useFloorTrading = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let on = true;
    const load = () => api.get("/tv/floor/trading").then((r) => on && setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { on = false; clearInterval(t); };
  }, []);
  return d;
};

const Stat = ({ label, value, sub, cls = "text-cyan-50", testid }) => (
  <div className="fc-panel px-[0.6em] py-[0.35em] min-w-0" data-testid={testid}>
    <p className="fc-label truncate">{label}</p>
    <p className={`nx-orbitron text-[1.55em] font-black leading-tight truncate ${cls}`}>{value}</p>
    {sub && <p className="fc-num text-[0.85em] text-cyan-200/60 truncate">{sub}</p>}
  </div>
);

const GoalBar = ({ label, value, goal, progress, testid }) => {
  const w = Math.max(0, Math.min(100, progress || 0));
  return (
    <div data-testid={testid}>
      <div className="flex justify-between fc-num text-[0.95em]"><span className="fc-label">{label}</span><b className={tone(progress)}>{value} / {goal} · {Number(progress || 0).toFixed(1)}%</b></div>
      <div className="h-[0.7em] rounded-full bg-white/5 overflow-hidden mt-[0.15em]">
        <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${w}%`, background: "linear-gradient(90deg,#F5C451,#2CF6B3)" }} />
      </div>
    </div>
  );
};

const Overall = ({ o, goals, id }) => (
  <section className="fc-panel p-[0.6em] flex flex-col gap-[0.45em] min-w-0" data-testid={`${id}-overall`}>
    <p className="fc-label">OVERALL · SINCE INCEPTION</p>
    <div className="grid grid-cols-4 gap-[0.4em]">
      <Stat label="$ P&L" value={sgn(o.pnl)} cls={tone(o.pnl)} testid={`${id}-overall-pnl`} />
      <Stat label="% RETURN" value={pct(o.return_pct)} cls={tone(o.return_pct)} sub={`on ${usd(o.base)}`} testid={`${id}-overall-return`} />
      <Stat label="WIN RATE" value={o.win_rate == null ? "--" : `${o.win_rate}%`} testid={`${id}-overall-winrate`} />
      <Stat label="TRADES" value={o.trades} sub={`${o.open} open`} testid={`${id}-overall-trades`} />
      <Stat label="BEST" value={o.best ? sgn(o.best.pnl) : "--"} sub={o.best?.symbol} cls="text-[#2CF6B3]" testid={`${id}-overall-best`} />
      <Stat label="WORST" value={o.worst ? sgn(o.worst.pnl) : "--"} sub={o.worst?.symbol} cls="text-[#FF3D68]" testid={`${id}-overall-worst`} />
      <Stat label="MAX DRAWDOWN" value={`-${usd(o.max_drawdown_usd)}`} sub={`${o.max_drawdown_pct}%`} cls={o.max_drawdown_usd ? "text-[#FF3D68]" : "text-cyan-50"} testid={`${id}-overall-dd`} />
      {goals ? <Stat label="FROM HIGH-WATER" value={`${goals.drawdown.pct}%`} sub={goals.drawdown.guard_tripped ? "GUARD TRIPPED · entries paused" : `guard ${goals.max_drawdown_pct}%`}
        cls={goals.drawdown.guard_tripped ? "text-[#FF3D68]" : "text-cyan-50"} testid={`${id}-overall-hwm`} /> : <div />}
    </div>
    {goals && <div className="flex flex-col gap-[0.35em]" data-testid={`${id}-goals`}>
      <GoalBar label="MONTHLY GOAL" value={sgn(goals.month.pnl, 0)} goal={usd(goals.monthly_goal_usd)} progress={goals.month.progress_pct} testid={`${id}-goal-monthly`} />
      <GoalBar label="ANNUAL GOAL" value={pct(goals.year.return_pct)} goal={`${goals.annual_goal_pct}%`} progress={goals.year.progress_pct} testid={`${id}-goal-annual`} />
    </div>}
  </section>
);

const Daily = ({ d, id }) => (
  <section className="fc-panel p-[0.6em] flex flex-col gap-[0.45em] min-w-0" data-testid={`${id}-daily`}>
    <p className="fc-label">DAILY · {d.day} · {d.session} · <span className={d.state === "LIVE" ? "text-[#2CF6B3]" : "text-amber-200"} data-testid={`${id}-daily-state`}>{d.state}</span></p>
    <div className="grid grid-cols-2 gap-[0.4em] flex-1">
      <Stat label="$ P&L" value={sgn(d.pnl)} cls={tone(d.pnl)} sub={`realized ${sgn(d.realized)} · open ${sgn(d.unrealized)}`} testid={`${id}-daily-pnl`} />
      <Stat label="% RETURN" value={pct(d.return_pct)} cls={tone(d.return_pct)} testid={`${id}-daily-return`} />
      <Stat label="TRADES" value={d.trades} sub={`${d.open} opened today still open`} testid={`${id}-daily-trades`} />
      <Stat label="WIN RATE" value={d.win_rate == null ? "--" : `${d.win_rate}%`} testid={`${id}-daily-winrate`} />
    </div>
    <p className="fc-num text-[0.9em] text-cyan-200/60" data-testid={`${id}-daily-carry`}>After-hours carried in: {sgn(d.carry_in.pnl)} · {d.carry_in.trades} trades (15:00–08:29 CT + weekend book here)</p>
  </section>
);

const COLS = "8.5em minmax(0,1fr) 4.5em 6em 6em 7em 6.5em";
const Row = ({ r, id, i }) => (
  <div className="grid fc-num text-[1em] py-[0.12em] border-b border-white/5 gap-[0.4em]" style={{ gridTemplateColumns: COLS }} data-testid={`${id}-trade-${i}`}>
    <span className="text-cyan-200/60 truncate">{r.time_ct}</span><b className="text-cyan-50 truncate">{r.symbol}</b>
    <span className={["long", "yes", "buy"].includes(r.side) ? "text-[#2CF6B3]" : "text-[#FF3D68]"}>{String(r.side || "").toUpperCase()}</span>
    <span className="text-cyan-100/70">{px(r.entry)}</span>
    {r.status === "OPEN" ? <b className="text-amber-200">OPEN</b> : <span className="text-cyan-100/70">{px(r.exit)}</span>}
    <b className={tone(r.pnl)}>{sgn(r.pnl)}</b><b className={tone(r.return_pct)}>{pct(r.return_pct)}</b>
  </div>
);

const Trades = ({ t, id }) => {
  const rows = [...t.open.slice(0, 6), ...t.closed];
  return (
    <section className="fc-panel min-h-0 flex-1 p-[0.6em] flex flex-col overflow-hidden" data-testid={`${id}-trades`}>
      <p className="fc-label mb-[0.2em]">TRADES · {t.open.length} OPEN · LAST {t.closed.length} CLOSED</p>
      <div className="grid fc-label border-b border-white/10 pb-[0.15em] gap-[0.4em]" style={{ gridTemplateColumns: COLS }}>
        {["TIME (CT)", "SYMBOL", "SIDE", "ENTRY", "EXIT", "$ P&L", "% RETURN"].map((h) => <span key={h}>{h}</span>)}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">{rows.map((r, i) => <Row key={`${r.symbol}-${i}`} r={r} id={id} i={i} />)}
        {!rows.length && <p className="fc-num text-cyan-200/60 py-[0.5em]">No trades on record yet.</p>}</div>
    </section>
  );
};

export const TradingSlide = ({ s, id, color, sub }) => (
  <div className="min-h-0 min-w-0 h-full flex flex-col gap-[0.5em] overflow-hidden" data-testid={`tv-${id}-screen`}>
    {!s ? <section className="fc-panel p-[0.7em] fc-num text-cyan-200/60">Loading…</section> : <>
      <div className="flex items-baseline gap-[0.8em]">
        <p className="nx-orbitron text-[2em] font-black tracking-[0.2em]" style={{ color, textShadow: `0 0 14px ${color}88` }} data-testid={`tv-${id}-title`}>{s.name.toUpperCase()}</p>
        <p className="fc-num text-[1em] text-cyan-200/70">{sub}</p>
      </div>
      <div className="grid gap-[0.5em]" style={{ gridTemplateColumns: "minmax(0,58fr) minmax(0,42fr)" }}>
        <Overall o={s.overall} goals={s.goals} id={id} /><Daily d={s.daily} id={id} />
      </div>
      <Trades t={s.trades} id={id} />
    </>}
  </div>
);

export const FloorTvAvV3 = ({ d }) => <TradingSlide s={d?.av_v3} id="av-v3" color="#00F0FF" sub="live House book · Coinbase · Alpaca · Kalshi · Tradier" />;
export const FloorTvHercules2 = ({ d }) => {
  const h = d?.hercules2;
  return <TradingSlide s={h} id="hercules2" color="#F5C451" sub={h ? `capital ${usd(h.capital)} · equity ${usd(h.equity, 2)} · paper book` : ""} />;
};
